// File upload settings
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const MAX_PRODUCTS_LIMIT = 5000;
export const ACCEPTED_FILE_TYPES = ["text/csv"];
export const ACCEPTED_FILE_EXTENSIONS = [".csv"];
export const MAX_DISPLAYED_ERRORS = 3;

// Required CSV headers
export const REQUIRED_CSV_HEADERS = ["product_name", "product_id"];

// URL for the column header specification image
export const COLUMN_HEADER_IMAGE_URL =
  "https://static.hginsights.com/platform/product_match_column_spec.png";

// UI Text
export const TEXT = {
  // Main component
  UPLOAD_TOOLTIP: "Bulk import products from CSV",
  UPLOAD_TOOLTIP_EXTENDED: (limit?: number) =>
    `File must contain:\n• product_name column\n• product_id column${limit ? `\n• Max ${limit.toLocaleString()} products` : ""}`,
  HELP_TOOLTIP: "CSV Format Guide",
  IMPORT_BADGE_LABEL: (count: number) => `${count} imported`,

  // Upload Dialog
  DIALOG_TITLE: "Import Products from CSV",
  DIALOG_CANCEL: "Cancel",
  DIALOG_IMPORT: "Import",
  DIALOG_UPLOAD_ANOTHER: "Upload Another",
  DIALOG_TRY_AGAIN: "Try Again",

  // Instructions
  UPLOAD_INSTRUCTIONS: (limit?: number) =>
    `Upload or Drag and Drop your CSV File.<br/>
    Column headers must include <strong>product_name</strong> and <strong>product_id</strong>.<br/>
    ${limit ? `Limit <strong>${limit}</strong> products per upload.` : ""}`,
  UPLOAD_BUTTON_TEXT: "Upload File",
  UPLOAD_POPOVER_INSTRUCTIONS: (limit?: number) =>
    `Please make sure your column headers include and use the exact spelling.<br/>
    Order of headers does not apply. Row data must include both the
    <strong>product_name</strong> and the associated
    <strong>product_id</strong> cells filled in.<br/><br/>
    ${limit ? `Limit <strong>${limit}</strong> products per upload.` : ""}`,
  COLUMN_HEADERS_ALT: "column-headers",

  DOWNLOAD_TEMPLATE: "Download Template",

  // Processing state
  PROCESSING_TITLE: "Processing file...",
  PROCESSING_SUBTITLE: "Validating products against catalog",

  // Error state
  ERROR_TITLE: "❌ Upload Failed",
  ERROR_VALIDATION_TITLE: "❌ File Validation Failed",
  ERROR_FORMAT_TITLE: "❌ File Format Error",
  ERROR_SUBTITLE: "We found some issues with your CSV file:",
  ERROR_FIX_MESSAGE: "Please fix the errors and try again",

  // Common errors
  ERROR_MISSING_HEADER: (header: string) =>
    `Missing required column: '${header}'`,
  ERROR_MISSING_HEADER_HELP:
    "Your file needs both 'product_name' and 'product_id' columns",
  ERROR_EMPTY_VALUE: (row: number, column: string) =>
    `Row ${row}: Empty ${column}`,
  ERROR_EMPTY_VALUES_HELP: (rows: number[]) =>
    `Rows ${rows.join(", ")} have missing product information`,
  ERROR_EXCEED_LIMIT: (count: number, limit: number) =>
    `File exceeds limit: ${count.toLocaleString()} products (max: ${limit.toLocaleString()})`,
  ERROR_EXCEED_LIMIT_HELP:
    "Please split your file or reduce the number of products",
  ERROR_NO_PRODUCTS_FOUND: "We couldn't match any products from your file",
  ERROR_NO_PRODUCTS_FOUND_HELP: `This might happen when:\n• Product IDs don't exist in our catalog\n• Products have been discontinued\n• IDs are from a different system`,
  ERROR_MORE_ITEMS: (count: number) => `...and ${count} more errors`,
  ERROR_CSV_VALIDATION: "CSV validation failed",
  TRY_AGAIN_BUTTON: "Try Again",

  // Snackbar
  SNACKBAR_SUCCESS: (count: number) =>
    `Successfully imported ${count} products`,
  SNACKBAR_EXPORT_SUCCESS: (count: number) =>
    `Successfully exported ${count} products`,

  // Export
  EXPORT_TOOLTIP_EXTENDED: (count: number) =>
    `Export ${count} selected product${count !== 1 ? "s" : ""} to CSV\n` +
    `File will include:\n• Product name\n• Product ID`,
  EXPORT_NO_SELECTION: "No products selected",

  // Import button
  IMPORT_TOOLTIP_EXTENDED_BUTTON: (limit?: number) =>
    `Import products from CSV file\nRequirements:\n• product_name column\n• product_id column${limit ? `\n• Max ${limit.toLocaleString()} products` : ""}`,
};

// CSV Template content
export const CSV_TEMPLATE_CONTENT = `product_name,product_id
"Example Product 1",12345
"Example Product 2",67890
"Add your products below following this format",`;

// Export CSV headers
export const EXPORT_CSV_HEADERS = ["product_name", "product_id"];

// Competitor-specific upload props
export const COMPETITOR_UPLOAD_PROPS = {
  dialogTitle: "Import Competitor Products",
  titleDescription: "Upload competitor products to analyze market presence.",
};

// Product list upload props
export const PRODUCT_LIST_UPLOAD_PROPS = {
  dialogTitle: "Import Product List",
  titleDescription: "Bulk upload a list of products",
};
